import React from "react";
import { Typography } from "@mui/material";
import "react-medium-image-zoom/dist/styles.css";
import "../JaldiLab.css";
import {
  getCircleTypeListItem,
  getTextContentHeading,
  getTextContentWithHeading,
  h1style,
  h2style,
  h3style,
  h4style,
  paraStyle,
  pointStyle,
  captionWithBorders,
  sectionStyle,
  subHeadingContent,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import JCimage1 from "../../../assets/Images/Judges chamber/JCimage 1.png";
import JCimage2 from "../../../assets/Images/Judges chamber/JCimage 2.png";
import JCimage3 from "../../../assets/Images/Judges chamber/JCimage 3.png";
import JCimage4 from "../../../assets/Images/Judges chamber/JCimage 4.png";
import Zoom from "react-medium-image-zoom";
import Divider from "@mui/material/Divider";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import judgeCorridor from "../../../assets/Images/Judges chamber/judgeCorridor.png";
import { FigureDesc45, FigureDesc46, FigureDesc47, FigureDesc48 } from "../../../utils/tmp/ImageDescription/JudgeChamberID";

const JudgeChamber = ({ goToSection }) => {
  const ObjectiveOject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Ensure that the Judges’ chamber is a
        <span style={{ color: "#DC002D" }}>
          {" "}
          comfortable space to work and to rest{" "}
        </span>
        in private;
      </>,
      <>
        Provide for adequate
        <span style={{ color: "#DC002D" }}>
          {" "}
          accessible and ergonomic furniture
        </span>
        , and
        <span style={{ color: "#DC002D" }}>
          {" "}
          accessible sanitary provisions{" "}
        </span>
        for the Judges;
      </>,

      <>
        Facilitate
        <span style={{ color: "#DC002D" }}> optimal utilisation of space </span>
        within the Chamber by organising the furniture layout efficiently; and
      </>,

      <>
        Enhance work efficiency by equipping the Judges’ Chambers with
        <span style={{ color: "#DC002D" }}> adequate IT infrastructure</span>,
        to ensure smooth functioning with the Court Staff as well as the
        stenographers.
      </>,
    ],
    isOrdered: false,
    isImage: true,
  };

  const usersObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Judge",
      "Stenographer",
      "Clerks",
      "Personal Secretary",
      "Vulnerable Witnesses, if required",
    ],
    isOrdered: false,
    isImage: true,
  };

  const nestedCircleItem = [
    "From the dais of the Courtroom; and",
    "To a secure Judges’ corridor leading up to the Courtroom or to the entrance of the Court Complex.",
  ];

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      <>
        The chamber must have two entrances for access:{" "}
        {getCircleTypeListItem(nestedCircleItem, true)}
      </>,

      <>
        The chamber must not be directly accessible to the public; therefore,
        access-based security guidelines must be implemented. For further
        details, please refer to
        <span
          style={sectionStyle}
          onClick={() =>
            goToSection("spacePlanningCirculation", "entrancesAndExits")
          }
        >
          {" "}
          Section 1.2.
        </span>
      </>,

      "The chamber must be in proximity to the Judges’ elevator, staircase and ramp, and the separate pathway leading to the Judges’ entrance/exit and parking area.",
    ],
    isOrdered: false,
    isImage: true,
  };

  const designConsiderationsObject = [
    {
      icon: <img src={designConsideration} />,
      heading: "Design Considerations",
      items: [
        "The chambers must be connected to accessible, family-friendly sanitary provisions including toilets and drinking water facilities. ",

        "The furniture layout in the Chamber must be efficiently organised, facilitating optimal utilisation of the space.",

        "Adequate space must be provided to accommodate a work area for stenographers and clerks. ",

        "Adequate panelled storage facilities must be provided for the storage of official files and personal belongings of the Judges.",

        "The Judge’s desk must be equipped with adequate technological devices to support communication and data networks, as well as multimedia systems including video-conferencing technology.",

        "Comfortable and ergonomic furniture must be provided.",

        "A separate sofa set and meeting area must be provided for instances where the deposition of Vulnerable Witnesses would have to be recorded within the chamber, or a meeting would have to be conducted with other Judges/administrative staff. ",

        "The Chamber must have access to adequate natural light and ventilation. ",

        "Access to each chamber must be controlled using a biometric system, with a manual punch number override.",

        <>
          The corridors for the chamber must be monitored 24/7 with CCTV cameras
          and trigger alarms especially for loitering/unattended baggage, beside
          regular security surveillance.
          <CaptionWithBorders1
            image={[JCimage2]}
            caption={"Figure 45: Typical floor plan for a Judge’s chamber"}
            isZoom={true}
            zoomImgStyle={{ height: "500px", objectFit: "contain" }}
            imageDescription={FigureDesc45}
          />
          <CaptionWithBorders1
            image={[JCimage1]}
            caption={"Figure 46: Representation of a Judge’s chamber"}
            isZoom={true}
            zoomImgStyle={{ height: "520px" }}
            imageDescription={FigureDesc46}
          />
        </>,
      ],
    },
    {
      heading: "PDJ Chamber",
      items: [
        "The PDJ Chamber must be larger than the chambers for other Judges to accommodate the additional functions to be undertaken. ",

        "The Chamber must be equipped with a conference table and chairs such that the PDJ may conduct meetings in their Chamber, if required.",

        "The Chamber must have adequate storage to accommodate the additional files that the PDJ may be required to store, pertaining to their administrative functions.",
      ],
    },
  ];

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 style={h2style}>
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANIZATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 id="headingFacilitiesJudge" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.4. </span>
          Facilities for Judges
        </h3>
        <h4 id="headingJudgeChamber" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.4.</span>A. Judges’ Chambers
        </h4>
        <p style={paraStyle}>
          Each Courtroom must be connected to a chamber for the Judge assigned
          to that Courtroom or to a corridor leading up to the Courtroom. The
          chamber functions as the Judge’s workspace as well as a retiring room
          over the course of the day. The chamber must therefore include both
          work-related facilities such as a dedicated space for stenographers
          and clerks to take down orders or perform other functions as directed
          by the Judge; access to case files, books and other legal amenities as
          required; and private, non-work related facilities such as an attached
          toilet, wardrobe for hanging coats, retiring area, etc.
        </p>
        {getTextContentHeading(ObjectiveOject)}
        {getTextContentHeading(usersObject)}
        {getTextContentHeading(adjacenciesObject)}
        {subHeadingContent(designConsiderationsObject)}
        <CaptionWithBorders1
          image={[judgeCorridor]}
          caption={"Figure 47: Typical floor plan for a PDJ chamber"}
          isZoom={true}
          zoomImgStyle={{ height: "500px" }}
          imageDescription={FigureDesc47}
        />
        <CaptionWithBorders1
          image={[JCimage3]}
          caption={"Figure 48: Representation of a Judge’s chamber"}
          isZoom={true}
          zoomImgStyle={{ height: "500px", objectFit: "contain" }}
          imageDescription={FigureDesc48}
        />
      </Typography>
    </div>
  );
};

export default JudgeChamber;
